import React, { useContext, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { getUserId } from '../../../services/DefaultService';
import logger from '../../../utils/logger';
import { SipClientStatus } from '../../../services/SipClientService/enums';
import { AppContext } from '../../../context/AppContextProvider';
import { ISipClientStatusDetailsProps } from '../../../services/SipClientService/types';

const STUN_URL = 'stun:stun.sipsorcery.com';

interface WebRTCPlayerProps {
  status: ISipClientStatusDetailsProps;
}

const WebRTCPlayer = ({ status }: WebRTCPlayerProps) => {
  const DEFAULT_WEBSOCKET_URL =
    //@ts-ignore
    `${window._env_.REACT_APP_WEB_RTC_NEGOTIATION}?userId=` +
    getUserId() +
    `&callId=${status.callInfos[0].callId}`;
  const { showSnackbar } = useContext(AppContext);
  const audioRef = useRef<HTMLAudioElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const start = async () => {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        localStreamRef.current = localStream;

        const pc = new RTCPeerConnection({ iceServers: [{ urls: STUN_URL }] });
        pcRef.current = pc;

        localStream.getTracks().forEach((track) => {
          pc.addTrack(track, localStream);
        });

        pc.ontrack = (evt) => {
          if (audioRef.current) {
            audioRef.current.srcObject = evt.streams[0];
          }
        };
        pc.onicecandidate = (evt) => {
          if (evt.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(evt.candidate));
          }
        };

        pc.onicegatheringstatechange = () => logger.debug('ICE Gathering State:', pc.iceGatheringState);
        pc.oniceconnectionstatechange = () => logger.debug('ICE Connection State:', pc.iceConnectionState);
        pc.onsignalingstatechange = () => logger.debug('Signaling State:', pc.signalingState);
        pc.onconnectionstatechange = () => logger.debug('Connection State:', pc.connectionState);

        const ws = new WebSocket(DEFAULT_WEBSOCKET_URL);
        wsRef.current = ws;

        ws.onmessage = async (evt) => {
          if (!pcRef.current) return;

          const data = JSON.parse(evt.data);
          if (data.candidate) {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(data));
          } else if (data.sdp) {
            await pcRef.current.setRemoteDescription(
              new RTCSessionDescription(data),
            );
            const answer = await pcRef.current.createAnswer();
            await pcRef.current.setLocalDescription(answer);
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(
                JSON.stringify(pcRef.current.localDescription),
              );
            }
          }
        };

        ws.onopen = () => logger.debug('WebSocket connection opened.');
        ws.onclose = () => logger.debug('WebSocket connection closed.');
        ws.onerror = (err) => logger.error('WebSocket error:', err);
      } catch (err) {
        showSnackbar(
          'Erro ao iniciar chamada, verifique se o microfone está conectado e se o seu dispositivo possui saída de áudio.',
          'error',
        );
        logger.error('Failed to start WebRTC connection:', err);
      }
    };

    const closePeer = () => {
      logger.debug('Closing all connections...');

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        localStreamRef.current = null;
        logger.debug('Local media stream stopped.');
      }

      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
        logger.debug('PeerConnection closed.');
      }

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        logger.debug('WebSocket closed.');
      }
    };

    if (
      status.status === SipClientStatus.ON_CALL ||
      status.status == SipClientStatus.CALL_WAITING
    ) {
      start();
    }

    return () => {
      closePeer();
    };
  }, [status.callInfos[0]]);

  return (
    <Box>
      <audio
        style={{
          position: 'absolute',
          left: '-9999px',
        }}
        autoPlay
        // playsInline
        ref={audioRef}
      />
    </Box>
  );
};

export default WebRTCPlayer;
